import React, { useId, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import {
  AppButton, Checkbox, FormField, HelpText, IconButton, Input, Label, NativeSelect, Textarea, cn,
} from '../ui';
import { uploadCmsImage } from '../../utils/cmsAdminApi';
import ImageField from './ImageField';
import CoursePicker from './CoursePicker';

// ─── PageContentField — renders one schema field of the Pages editor ─────
//
// Driven entirely by the schema entry (./pageSchemas). Supported `type`s:
//   text · textarea · number · select · icon · toggle · link · image
//   courses · repeater (which nests: a row may hold another repeater)
//
// Value in, onChange out; the only state here is transient (an upload's
// preview), so the workspace draft stays the single source of truth.
//
// Layout: fields sit in the two-column grid of their section. `isWideField`
// decides which take the full row.

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const normaliseOptions = (options) =>
  (Array.isArray(options) ? options : []).map((o) =>
    isPlainObject(o) ? { value: o.value, label: o.label ?? o.value } : { value: o, label: o }
  );

const WIDE_TYPES = new Set(['textarea', 'repeater', 'courses', 'link', 'toggle']);

/** Whether a field takes its section's full width rather than one column. */
export const isWideField = (field) =>
  !!field.wide || WIDE_TYPES.has(field.type) || (field.type === 'image' && !field.compact);

// Row layouts for a repeater's fields. A closed set, so each resolves to one
// literal class the Tailwind build can see.
const ROW_COLUMN_CLASSES = {
  1: '',
  2: 'min-[721px]:tw-grid-cols-2',
  3: 'min-[721px]:tw-grid-cols-3',
};

// An uploaded image is saved as its storage key, which only becomes a
// viewable URL when the content is next loaded. Remember the URL the upload
// returned so the preview appears straight away — module-level so it
// survives a row moving to another position in its list.
const uploadedPreviews = new Map();

// Our own stored files come back as signed URLs; showing one in the "paste a
// link" box would invite editing a URL that expires.
const isStoredFile = (v) => v.startsWith('institutes/') || v.includes('/institutes/');
const isViewable = (v) => /^(https?:)?\/\//.test(v) || v.startsWith('data:');

function PageImageField({ field, value, onChange, onShowToast }) {
  const [busy, setBusy] = useState(false);
  const cur = typeof value === 'string' ? value : '';
  const preview = isViewable(cur) ? cur : (uploadedPreviews.get(cur) || '');

  const onFile = async (file) => {
    setBusy(true);
    try {
      const res = await uploadCmsImage(file, 'page-media');
      const stored = res.key || res.url;
      if (!stored) throw new Error('upload failed');
      if (res.key && res.url) uploadedPreviews.set(res.key, res.url);
      onChange(stored);
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || 'The image could not be uploaded.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ImageField
      label={field.label}
      hint={field.hint}
      compact={field.compact}
      previewUrl={preview}
      urlValue={cur && !isStoredFile(cur) ? cur : ''}
      onUrlChange={onChange}
      onFile={onFile}
      onRemove={() => onChange('')}
      busy={busy}
    />
  );
}

function LinkField({ field, value, onChange }) {
  const id = useId();
  const link = isPlainObject(value) ? value : {};
  const patch = (p) => onChange({ ...link, ...p });
  return (
    <div role="group" aria-labelledby={`${id}-label`} className="tw-flex tw-flex-col tw-gap-1.5">
      <Label id={`${id}-label`} htmlFor={`${id}-text`}>{field.label}</Label>
      <div className="tw-grid tw-grid-cols-1 tw-gap-2 min-[721px]:tw-grid-cols-2">
        <Input
          id={`${id}-text`}
          placeholder={field.placeholder || 'Button text'}
          value={link.label || ''}
          onChange={(e) => patch({ label: e.target.value })}
        />
        <Input
          aria-label={`${field.label} — link address`}
          placeholder={field.hrefPlaceholder || '/contact'}
          value={link.href || ''}
          onChange={(e) => patch({ href: e.target.value })}
        />
      </div>
      <Checkbox
        size="sm"
        label="Open in a new tab"
        checked={!!link.newTab}
        onChange={(e) => patch({ newTab: e.target.checked })}
      />
      {field.hint && <HelpText>{field.hint}</HelpText>}
    </div>
  );
}

function RepeaterField({ field, value, onChange, depth, courses, onShowToast }) {
  const raw = Array.isArray(value) ? value : [];
  const rows = field.coerceRow ? raw.map(field.coerceRow) : raw;
  const itemFields = field.item || [];
  const min = field.min ?? 0;
  const max = field.max ?? 50;
  const itemLabel = field.itemLabel || 'Item';
  const columns = field.inline ? Math.min(field.columns || itemFields.length, 3) : 1;

  const patchRow = (i, patch) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...(isPlainObject(r) ? r : {}), ...patch } : r)));
  const moveRow = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-flex-col tw-gap-0.5">
        <span className="tw-text-sm tw-font-medium tw-text-content">{field.label}</span>
        {field.hint && <HelpText>{field.hint}</HelpText>}
      </div>

      {rows.length === 0 && (
        <p className="tw-m-0 tw-rounded-sm tw-border tw-border-dashed tw-border-line tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-subtle">
          {field.emptyText || `No ${itemLabel.toLowerCase()}s yet.`}
        </p>
      )}

      {rows.map((row, i) => {
        const rowObj = isPlainObject(row) ? row : {};
        const preview = field.titleKey ? String(rowObj[field.titleKey] || '').trim() : '';
        return (
          // Index-keyed: rows carry no stable id, and position is what keeps
          // the focused input in place when a row above it moves.
          <div
            key={i}
            className={cn(
              'tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-3',
              depth ? 'tw-bg-surface-raised' : 'tw-bg-surface-sunken'
            )}
          >
            <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2">
              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-xs tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
                {itemLabel} {i + 1}
                {preview && <span className="tw-font-normal tw-normal-case tw-tracking-normal"> — {preview}</span>}
              </span>
              <IconButton size="xs" icon={ChevronUp} label={`Move ${itemLabel.toLowerCase()} ${i + 1} up`} onClick={() => moveRow(i, -1)} disabled={i === 0} />
              <IconButton size="xs" icon={ChevronDown} label={`Move ${itemLabel.toLowerCase()} ${i + 1} down`} onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1} />
              <IconButton
                size="xs"
                icon={Trash2}
                variant="ghost-destructive"
                label={rows.length <= min ? `At least ${min} required` : `Remove ${itemLabel.toLowerCase()} ${i + 1}`}
                onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
                disabled={rows.length <= min}
              />
            </div>
            <div className={cn('tw-grid tw-grid-cols-1 tw-gap-x-4 tw-gap-y-3', ROW_COLUMN_CLASSES[columns])}>
              {itemFields.map((sub) => (
                <div key={sub.key} className={columns > 1 && isWideField(sub) ? 'tw-col-span-full' : undefined}>
                  <PageContentField
                    field={sub}
                    value={rowObj[sub.key]}
                    onChange={(v) => patchRow(i, { [sub.key]: v })}
                    onShowToast={onShowToast}
                    courses={courses}
                    depth={depth + 1}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <AppButton
          size="sm"
          variant="outline"
          onClick={() => onChange([...rows, { ...(field.defaultItem || {}) }])}
          disabled={rows.length >= max}
        >
          <Plus size={16} /> {field.addLabel || `Add ${itemLabel.toLowerCase()}`}
        </AppButton>
        {rows.length >= max && (
          <HelpText>The website shows up to {max} — remove one to add another.</HelpText>
        )}
      </div>
    </div>
  );
}

export default function PageContentField({
  field,
  value,
  onChange,
  onShowToast,
  courses = [],
  depth = 0,
}) {
  switch (field.type) {
    case 'repeater':
      return (
        <RepeaterField
          field={field}
          value={value}
          onChange={onChange}
          depth={depth}
          courses={courses}
          onShowToast={onShowToast}
        />
      );

    case 'courses':
      return (
        <CoursePicker
          label={field.label}
          hint={field.hint}
          max={field.max}
          courses={courses}
          value={value}
          onChange={onChange}
        />
      );

    case 'image':
      return <PageImageField field={field} value={value} onChange={onChange} onShowToast={onShowToast} />;

    case 'link':
      return <LinkField field={field} value={value} onChange={onChange} />;

    case 'toggle': {
      // Unset means "never touched": fall back to the schema default rather
      // than reading absence as off, or every untouched element would hide.
      const isOn = value === undefined || value === null || value === ''
        ? field.default !== false
        : value !== false && value !== 'false';
      return (
        <Checkbox
          label={field.label}
          description={field.hint}
          checked={isOn}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    }

    case 'select':
    case 'icon':
      return (
        <FormField label={field.label} hint={field.hint}>
          <NativeSelect value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            <option value="">{field.emptyOption || (field.type === 'icon' ? 'No icon' : 'Default')}</option>
            {normaliseOptions(field.options).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </NativeSelect>
        </FormField>
      );

    case 'number':
      return (
        <FormField label={field.label} hint={field.hint}>
          <Input
            type="number"
            min={field.min}
            max={field.max}
            placeholder={field.placeholder || ''}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </FormField>
      );

    case 'textarea': {
      const cur = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
      return (
        <FormField label={field.label} hint={field.hint}>
          <Textarea
            rows={field.rows || 3}
            maxLength={field.maxLength}
            placeholder={field.placeholder || ''}
            value={cur}
            onChange={(e) => onChange(e.target.value)}
          />
        </FormField>
      );
    }

    default: {
      const cur = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
      return (
        <FormField label={field.label} hint={field.hint}>
          <Input
            maxLength={field.maxLength}
            placeholder={field.placeholder || ''}
            value={cur}
            onChange={(e) => onChange(e.target.value)}
          />
        </FormField>
      );
    }
  }
}

/** Field types whose value is a plain string — the only ones AI drafting may fill. */
export const TEXTUAL_FIELD_TYPES = new Set(['text', 'textarea', undefined]);
